import type { InitOptions } from 'i18next';

/**
 * Get i18n settings for i18next.
 * @param languages
 * @param language
 * @param namespaces
 */
export function createI18nSettings({
  languages,
  language,
  namespaces,
}: {
  languages: string[];
  language: string;
  namespaces?: string | string[];
}): InitOptions {
  const lng = language;
  const ns = namespaces;

  return {
    supportedLngs: languages,
    fallbackLng: languages[0],
    detection: undefined,
    showSupportNotice: false,
    lng,
    preload: false as const,
    /**
     * MUST stay false. `lowerCaseLng: true` lowercases the active language
     * before i18next matches it against `supportedLngs`, and `zh-CN` is the
     * only locale here whose canonical form is not already lowercase. The
     * lowercased `zh-cn` misses the literal `'zh-CN'` in `supportedLngs`, so
     * i18next resolves nothing and falls back wholesale to `en` — server AND
     * client, since both build settings from this function. Net symptom: a
     * page served as `<html lang="zh-CN">` containing English.
     *
     * Dormant from the day it was set (lowercasing is the identity for the
     * other 14 locales) and armed on 2026-06-08 by `b6bf4a7b19`, which added
     * `zh-CN` as the first mixed-case locale. The repo standardizes on the
     * capitalized form everywhere else — shops schemas, `parity-ratchet.mjs`,
     * `scaffold-locales.mjs`, and the on-disk `public/locales/zh-CN/` — so
     * false is what aligns i18next with the existing convention.
     */
    lowerCaseLng: false as const,
    // Only use 'common' as fallback namespace to avoid cross-namespace key collisions
    // (e.g., admin:title and events:title both exist, causing wrong translations)
    fallbackNS: 'common',
    missingInterpolationHandler: (text, value, options) => {
      if (process.env.NODE_ENV !== 'production') {
        console.debug(
          `Missing interpolation value for key: ${text}`,
          value,
          options,
        );
      }
    },
    ns,
    react: {
      useSuspense: true,
    },
  };
}
